import * as yup from 'yup';
import { useState } from 'react';
import { Container } from "react-bootstrap";
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { yupResolver } from '@hookform/resolvers/yup';
import AddressTable from "../forms/components/AddressTable";
import { createAddress, getAddress, removeAddress, updateDefaultAddress } from '../api/Address.api';

const addressSchema = yup.object().shape({
  company: yup.string().required('Name is required'),
  street1: yup.string().required('Number & Street is required'),
  suburb: yup.string().required('Suburb is required'),
  state: yup.string().required('State is required'),
  postcode: yup.string().required('Postcode is required'),
});

const checkStyle = {
  grey: {
    cursor: 'pointer',
    color: 'grey',
    zIndex: 10
  },
  green: {
    cursor: 'pointer',
    color: 'green',
    zIndex: 10
  }
}

function AddressBook() {
  /** status can be error, success, warning using Alert component */
  const [statusMessage, setStatusMessage] = useState({ message: '', status: 'success' });
  const [isAdding, setIsAdding] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'shipping'|'billing'|'delete', address }
  const [isOperating, setIsOperating] = useState(false);

  const { isLoading, error, data: addresses, refetch: refetchAddresses } = useQuery({
    queryKey: ['address-book'],
    queryFn: getAddress,
    staleTime: 1000 * 60 * 30, // 30 minutes
    defaultValues: []
  });

  const methods = useForm({
    resolver: yupResolver(addressSchema),
    defaultValues: {
      company: '',
      street1: '',
      suburb: '',
      state: '',
      postcode: ''
    }
  })
  const { reset, clearErrors, handleSubmit } = methods;

  const addTableRow = (e) => {
    e.preventDefault();
    setIsAdding(true);
    setStatusMessage({});
  }

  const deleteTableRow = () => {
    clearErrors();
    setIsAdding(false);
  }

  const onSubmit = async (data) => {
    try {
      const response = await createAddress(data);
      if (response.status === 200) {
        setStatusMessage({ message: 'Your address was saved.', status: 'success' });
        handleReset();
      }
    } catch (error) {
      setStatusMessage({ message: 'Your address was not saved.', status: 'error' });
      console.log(error);
    } finally {
      deleteTableRow();
      refetchAddresses();
    }
  };

  const handleAddressDefault = async (id, method) => {
    await updateDefaultAddress({ id, method });
    setStatusMessage({ message: 'Default ' + method + ' address was flagged.', status: 'success' });
    refetchAddresses();
  };

  const handleAddressRemove = async (id) => {
    const response = await removeAddress({ id });
    if (response.status === 200) {
      setStatusMessage({ message: 'Your address has been deleted', status: 'success' });
    }
    refetchAddresses();
  };

  const handleReset = () => {
    reset();
  }

  const handleConfirm = async () => {
    const { type, address } = pendingAction;
    setIsOperating(true);
    try {
      if (type === 'delete') {
        await handleAddressRemove(address.id);
      } else {
        await handleAddressDefault(address.id, type);
      }
    } finally {
      setIsOperating(false);
      setPendingAction(null);
    }
  };

  if (error) console.log(error);

  return (
    <Container>
      <div className="mb-5">
        <h2>Address Book</h2>
        <p>To make the order process quicker, iStore Shop provides a way to store your most common delivery addresses for a more convenient checkout process.</p>
        {statusMessage.message && <Alert className="border mb-3" severity={statusMessage.status}>{statusMessage.message}</Alert>}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <table className="" id="my-orders-table">
              <thead>
                <tr className="text-black">
                  <th className="id" colSpan="5"></th>
                  <th className="address-tab text-center bg-secondary bg-opacity-50" colSpan="2">Default Addresses</th>
                  <th className="blank"></th>
                </tr>
                <tr className="text-black">
                  <th className="date">Name</th>
                  <th className="date">Number & Street</th>
                  <th style={{width: '20%'}} className="total">Suburb</th>
                  <th style={{width: '10%'}} className="status text-center">State</th>
                  <th style={{width: '10%'}} className="status text-center">Postcode</th>
                  <th style={{width: '85px'}} className="actions text-center border">Shipping</th>
                  <th style={{width: '85px'}} className="actions text-center border">Billing</th>
                  <th style={{width: '30px'}} className="actions"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8}>
                      <div className='d-flex justify-content-center'>
                        <CircularProgress/>
                      </div>
                    </td>
                  </tr>
                ) : (
                  addresses?.map((address)  => {
                    return (
                      <tr key={address.id}>
                        <td className="border">{address.company}</td>
                        <td className="border">{address.street1}</td>
                        <td className="border">{address.suburb}</td>
                        <td className="border">{address.state}</td>
                        <td className="border">{address.postcode}</td>
                        <td style={{ width: '85px' }} className="border text-center">
                          <button
                            className='bg-transparent border-0'
                            title="Set this address as default Shipping address"
                            disabled={isAdding}
                            onClick={(e) => { e.preventDefault(); setPendingAction({ type: 'shipping', address }); }}
                          >
                            <i className="fas fa-check-circle" style={address.shipping ? checkStyle.green : checkStyle.grey} />
                          </button>
                        </td>

                        <td style={{ width: '85px' }} className="border text-center">
                          <button
                            className='bg-transparent border-0'
                            title="Set this address as default Billing address"
                            disabled={isAdding}
                            onClick={(e) => { e.preventDefault(); setPendingAction({ type: 'billing', address }); }}
                          >
                            <i className="fas fa-check-circle" style={address.billing ? checkStyle.green : checkStyle.grey} />
                          </button>
                        </td>

                        <td style={{width: '60px'}} data-th="Actions" className="border">
                          <button
                            className='bg-transparent border-0'
                            title="Delete this address"
                            disabled={isAdding}
                            onClick={(e) => { e.preventDefault(); setPendingAction({ type: 'delete', address }); }}
                          >
                            <i className="fas fa-trash"/>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
                <AddressTable isAdding={isAdding} deleteTableRow={deleteTableRow} addTableRow={addTableRow} />
              </tbody>
            </table>
          </form>
        </FormProvider>
      </div>
      {pendingAction && (
        <Dialog open onClose={() => setPendingAction(null)}>
          <DialogTitle>
            {pendingAction.type === 'delete'
              ? 'Delete Address'
              : `Set as Default ${pendingAction.type === 'shipping' ? 'Shipping' : 'Billing'} Address`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {pendingAction.type === 'delete'
                ? 'Are you sure you want to delete this address? This action cannot be undone.'
                : `Set the following address as your default ${pendingAction.type} address?`}
            </DialogContentText>
            <DialogContentText sx={{ mt: 1, fontWeight: 500, color: 'text.primary' }}>
              {pendingAction.address.company} — {pendingAction.address.street1}, {pendingAction.address.suburb} {pendingAction.address.state} {pendingAction.address.postcode}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingAction(null)} disabled={isOperating}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              color={pendingAction.type === 'delete' ? 'error' : 'primary'}
              variant="contained"
              disableElevation
              disabled={isOperating}
              startIcon={isOperating ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {pendingAction.type === 'delete' ? 'Delete' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}

export default AddressBook;